import type { ChatServicePort, ChatSnapshot } from '../services/chat';
import type { RealtimeDatabasePort } from '../services/firebase';
import type { ChatBatchView, ChatUiPort } from '../ui/chat-dialog';

function toBatch(snapshot: ChatSnapshot): ChatBatchView {
  return Object.freeze({
    messages: Object.freeze(snapshot.messages.map((message) => Object.freeze({
      id: message.id,
      uid: message.uid,
      name: message.name,
      text: message.text,
      createdAt: message.timestamp,
    }))),
    hasMore: snapshot.hasMoreHistory,
  });
}

export class ChatUiAdapter implements ChatUiPort {
  constructor(
    private readonly service: ChatServicePort,
    private readonly database: RealtimeDatabasePort,
  ) {}

  get currentUserId(): string | null {
    return this.database.availability.uid;
  }

  async connect(
    onMessages: (batch: ChatBatchView) => void,
    onError: () => void,
  ): Promise<'connected' | 'disabled'> {
    const snapshot = await this.service.open((next) => {
      if (next.state === 'error' || next.state === 'offline') onError();
      if (next.state === 'connected') onMessages(toBatch(next));
    });
    if (snapshot.state === 'error' || snapshot.state === 'offline') return 'disabled';
    return 'connected';
  }

  disconnect(): void {
    this.service.close();
  }

  async loadOlder(): Promise<ChatBatchView> {
    await this.service.loadOlder();
    return toBatch(this.service.snapshot);
  }

  async send(username: string, message: string): Promise<void> {
    await this.service.send(username, message);
  }
}
